import React, { useEffect, useState } from "react";
import { api, normalizeImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Save, X } from "lucide-react";

export default function CrudPage({ title, endpoint, fields, testPrefix }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/${endpoint}`).then(r => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);
  const startNew = () => setEditing(Object.fromEntries(fields.map(f => [f.key, ""])));
  const save = async () => {
    try {
      const payload = { ...editing };
      if (payload.photo_url) payload.photo_url = normalizeImageUrl(payload.photo_url);
      if (payload.image_url) payload.image_url = normalizeImageUrl(payload.image_url);
      if (editing.id) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success("Tersimpan"); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { if (!confirm("Hapus?")) return; await api.delete(`/${endpoint}/${id}`); toast.success("Terhapus"); load(); };
  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-3xl font-extrabold text-emerald-950">{title}</h1>
        <button onClick={startNew} data-testid={`${testPrefix}-new-btn`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-emerald-950 font-bold text-sm"><Plus className="w-4 h-4" /> Tambah</button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-600">
            <tr>{fields.slice(0, 3).map(f => <th key={f.key} className="text-left px-4 py-3">{f.label}</th>)}<th className="text-right px-4 py-3">Aksi</th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-slate-100">
                {fields.slice(0, 3).map(f => <td key={f.key} className="px-4 py-3 text-slate-800">{String(it[f.key] || "-").substring(0, 80)}</td>)}
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(it)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-emerald-800 hover:bg-emerald-50"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(it.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-red-700 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={fields.slice(0,3).length + 1} className="text-center py-10 text-slate-500">Belum ada data.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b flex justify-between items-center"><h2 className="font-display font-extrabold text-xl">{editing.id ? "Edit" : "Tambah"} {title}</h2><button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button></div>
            <div className="p-6 space-y-4">
              {fields.map(f => (
                <label key={f.key} className="block">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">{f.label}</div>
                  {f.type === "textarea"
                    ? <textarea rows="4" value={editing[f.key] || ""} onChange={e => setEditing(v => ({ ...v, [f.key]: e.target.value }))} className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-emerald-700 outline-none text-sm" />
                    : <input value={editing[f.key] || ""} onChange={e => setEditing(v => ({ ...v, [f.key]: e.target.value }))} className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-emerald-700 outline-none text-sm" />}
                  {(f.key === "photo_url" || f.key === "image_url") && editing[f.key] && (
                    <div className="mt-2 flex items-center gap-3">
                      <img src={normalizeImageUrl(editing[f.key])} alt="preview" className="w-20 h-20 rounded-lg object-cover border border-slate-200 bg-slate-50"
                        onError={e => { e.currentTarget.style.display = "none"; }} onLoad={e => { e.currentTarget.style.display = "block"; }} />
                      <span className="text-xs text-slate-500">Preview — link Google Drive akan otomatis dikonversi saat disimpan.</span>
                    </div>
                  )}
                </label>
              ))}
              <div className="flex justify-end gap-2 pt-4">
                <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border-2 border-slate-200 font-bold text-sm">Batal</button>
                <button onClick={save} data-testid={`${testPrefix}-save-btn`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-950 text-white font-bold text-sm"><Save className="w-4 h-4" /> Simpan</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
